import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  CloudArrowUpIcon, 
  CogIcon, 
  ServerIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import Navbar from './Navbar';

const DeployModule = () => {
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deploymentStep, setDeploymentStep] = useState(1);
  const [deploymentConfig, setDeploymentConfig] = useState({
    name: '',
    description: '',
    environment: 'production',
    region: 'us-east-1',
    instanceType: 't3.medium',
    autoScaling: true,
    monitoring: true
  });

  const moduleTemplates = [
    {
      id: 'ai-assistant',
      name: 'AI Assistant',
      description: 'Conversational AI agent with natural language processing',
      category: 'AI/ML',
      icon: '🤖',
      complexity: 'Medium',
      estimatedTime: '15-20 min',
      resources: { cpu: '2 vCPU', memory: '4 GB', storage: '20 GB' },
      features: ['Natural Language Processing', 'Context Awareness', 'Multi-language Support'],
      popular: true
    },
    {
      id: 'data-pipeline',
      name: 'Data Pipeline',
      description: 'ETL pipeline for data processing and transformation',
      category: 'Data',
      icon: '📊',
      complexity: 'High',
      estimatedTime: '25-30 min',
      resources: { cpu: '4 vCPU', memory: '8 GB', storage: '100 GB' },
      features: ['Real-time Processing', 'Data Validation', 'Error Handling'],
      popular: false
    },
    {
      id: 'api-gateway',
      name: 'API Gateway',
      description: 'Secure API management and routing service',
      category: 'Infrastructure',
      icon: '🌐',
      complexity: 'Low',
      estimatedTime: '10-15 min',
      resources: { cpu: '1 vCPU', memory: '2 GB', storage: '10 GB' },
      features: ['Rate Limiting', 'Authentication', 'Load Balancing'],
      popular: true
    },
    {
      id: 'workflow-engine',
      name: 'Workflow Engine',
      description: 'Automated workflow orchestration and management',
      category: 'Automation',
      icon: '⚙️',
      complexity: 'High',
      estimatedTime: '20-25 min',
      resources: { cpu: '4 vCPU', memory: '6 GB', storage: '50 GB' },
      features: ['Visual Workflow Builder', 'Conditional Logic', 'Error Recovery'],
      popular: true
    },
    {
      id: 'monitoring-dashboard',
      name: 'Monitoring Dashboard',
      description: 'Real-time system monitoring and alerting',
      category: 'Observability',
      icon: '📈',
      complexity: 'Medium',
      estimatedTime: '15-20 min',
      resources: { cpu: '2 vCPU', memory: '4 GB', storage: '30 GB' },
      features: ['Real-time Metrics', 'Custom Alerts', 'Historical Data'],
      popular: false
    },
    {
      id: 'custom-module',
      name: 'Custom Module',
      description: 'Build your own module from scratch',
      category: 'Custom',
      icon: '🛠️',
      complexity: 'Variable',
      estimatedTime: 'Variable',
      resources: { cpu: 'Configurable', memory: 'Configurable', storage: 'Configurable' },
      features: ['Full Customization', 'Custom APIs', 'Flexible Architecture'],
      popular: false
    }
  ];

  const environments = [
    { id: 'development', name: 'Development', description: 'For testing and development' },
    { id: 'staging', name: 'Staging', description: 'Pre-production environment' },
    { id: 'production', name: 'Production', description: 'Live production environment' }
  ];

  const regions = [
    { id: 'us-east-1', name: 'US East (N. Virginia)', flag: '🇺🇸' },
    { id: 'us-west-2', name: 'US West (Oregon)', flag: '🇺🇸' },
    { id: 'eu-west-1', name: 'Europe (Ireland)', flag: '🇪🇺' },
    { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore)', flag: '🌏' }
  ];

  const instanceTypes = [
    { id: 't3.micro', name: 't3.micro', cpu: '2 vCPU', memory: '1 GB', price: '$0.0104/hour' },
    { id: 't3.small', name: 't3.small', cpu: '2 vCPU', memory: '2 GB', price: '$0.0208/hour' },
    { id: 't3.medium', name: 't3.medium', cpu: '2 vCPU', memory: '4 GB', price: '$0.0416/hour' },
    { id: 't3.large', name: 't3.large', cpu: '2 vCPU', memory: '8 GB', price: '$0.0832/hour' }
  ];

  const handleConfigChange = (key, value) => {
    setDeploymentConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDeploy = () => {
    // Simulate deployment process
    setDeploymentStep(4);
    setTimeout(() => {
      setDeploymentStep(5);
    }, 3000);
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'Low': return 'text-green-400 bg-green-400/10';
      case 'Medium': return 'text-yellow-400 bg-yellow-400/10';
      case 'High': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      {/* Header */}
      <Navbar variant="transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white">Services</Link>
            <span>/</span>
            <span className="text-white">Deploy Module</span>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4 mb-6">
            {[
              { step: 1, title: 'Select Template' },
              { step: 2, title: 'Configure' },
              { step: 3, title: 'Review' },
              { step: 4, title: 'Deploy' },
              { step: 5, title: 'Complete' }
            ].map((item, index) => (
              <div key={item.step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  deploymentStep >= item.step 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {deploymentStep > item.step ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    item.step
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  deploymentStep >= item.step ? 'text-white' : 'text-gray-400'
                }`}>
                  {item.title}
                </span>
                {index < 4 && (
                  <div className={`w-12 h-0.5 mx-4 ${
                    deploymentStep > item.step ? 'bg-primary' : 'bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Template Selection */}
        {deploymentStep === 1 && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-white mb-4">Deploy New Module</h1>
              <p className="text-xl text-gray-300">Choose a template to get started</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {moduleTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`relative bg-gray-800 rounded-xl p-6 cursor-pointer transition-all duration-200 border-2 ${
                    selectedTemplate?.id === template.id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {template.popular && (
                    <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-full font-bold">
                      Popular
                    </div>
                  )}
                  
                  <div className="text-4xl mb-4">{template.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{template.name}</h3>
                  <p className="text-gray-300 mb-4">{template.description}</p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Category:</span>
                      <span className="text-sm text-white">{template.category}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Complexity:</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getComplexityColor(template.complexity)}`}>
                        {template.complexity}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Deploy Time:</span>
                      <span className="text-sm text-white">{template.estimatedTime}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="text-xs text-gray-400 mb-2">Resources:</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-white font-bold">{template.resources.cpu}</div>
                        <div className="text-gray-400">CPU</div>
                      </div>
                      <div className="text-center">
                        <div className="text-white font-bold">{template.resources.memory}</div>
                        <div className="text-gray-400">Memory</div>
                      </div>
                      <div className="text-center">
                        <div className="text-white font-bold">{template.resources.storage}</div>
                        <div className="text-gray-400">Storage</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={() => selectedTemplate && setDeploymentStep(2)}
                disabled={!selectedTemplate}
                className="bg-primary text-white px-8 py-3 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
              >
                Continue with {selectedTemplate?.name || 'Selected Template'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Configuration */}
        {deploymentStep === 2 && selectedTemplate && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-white mb-4">Configure {selectedTemplate.name}</h1>
              <p className="text-xl text-gray-300">Set up your deployment parameters</p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Configuration */}
                <div className="bg-gray-800 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-6">Basic Configuration</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Module Name</label>
                      <input
                        type="text"
                        value={deploymentConfig.name}
                        onChange={(e) => handleConfigChange('name', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder={`My ${selectedTemplate.name}`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        value={deploymentConfig.description}
                        onChange={(e) => handleConfigChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Describe your module..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Environment</label>
                      <select
                        value={deploymentConfig.environment}
                        onChange={(e) => handleConfigChange('environment', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {environments.map(env => (
                          <option key={env.id} value={env.id}>{env.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Infrastructure Configuration */}
                <div className="bg-gray-800 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-6">Infrastructure</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Region</label>
                      <select
                        value={deploymentConfig.region}
                        onChange={(e) => handleConfigChange('region', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {regions.map(region => (
                          <option key={region.id} value={region.id}>
                            {region.flag} {region.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Instance Type</label>
                      <select
                        value={deploymentConfig.instanceType}
                        onChange={(e) => handleConfigChange('instanceType', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {instanceTypes.map(instance => (
                          <option key={instance.id} value={instance.id}>
                            {instance.name} - {instance.cpu}, {instance.memory} ({instance.price})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-300">Auto Scaling</span>
                        <button
                          onClick={() => handleConfigChange('autoScaling', !deploymentConfig.autoScaling)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            deploymentConfig.autoScaling ? 'bg-primary' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              deploymentConfig.autoScaling ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-300">Monitoring</span>
                        <button
                          onClick={() => handleConfigChange('monitoring', !deploymentConfig.monitoring)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            deploymentConfig.monitoring ? 'bg-primary' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              deploymentConfig.monitoring ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={() => setDeploymentStep(1)}
                  className="bg-gray-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-600 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setDeploymentStep(3)}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-600 transition-colors"
                >
                  Review Configuration
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {deploymentStep === 3 && selectedTemplate && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-white mb-4">Review Deployment</h1>
              <p className="text-xl text-gray-300">Confirm your configuration before deployment</p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-gray-800 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-white mb-4">Deployment Summary</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-white mb-3">Module Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Template:</span>
                        <span className="text-white">{selectedTemplate.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Name:</span>
                        <span className="text-white">{deploymentConfig.name || `My ${selectedTemplate.name}`}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Environment:</span>
                        <span className="text-white capitalize">{deploymentConfig.environment}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Infrastructure</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Region:</span>
                        <span className="text-white">{regions.find(r => r.id === deploymentConfig.region)?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Instance:</span>
                        <span className="text-white">{deploymentConfig.instanceType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Auto Scaling:</span>
                        <span className="text-white">{deploymentConfig.autoScaling ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-700 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-400 mb-1">Important Notes</h4>
                    <ul className="text-sm text-yellow-200 space-y-1">
                      <li>• Deployment will take approximately {selectedTemplate.estimatedTime}</li>
                      <li>• Resources will be provisioned in {regions.find(r => r.id === deploymentConfig.region)?.name}</li>
                      <li>• You will be charged based on actual usage</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setDeploymentStep(2)}
                  className="bg-gray-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-600 transition-colors"
                >
                  Back to Configuration
                </button>
                <button
                  onClick={handleDeploy}
                  className="bg-primary text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-600 transition-colors flex items-center space-x-2"
                >
                  <CloudArrowUpIcon className="h-5 w-5" />
                  <span>Deploy Module</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Deploying */}
        {deploymentStep === 4 && (
          <div className="text-center">
            <div className="max-w-2xl mx-auto">
              <div className="animate-spin w-16 h-16 border-4 border-primary border-t-transparent rounded-full mx-auto mb-6"></div>
              <h1 className="text-4xl font-bold text-white mb-4">Deploying Module</h1>
              <p className="text-xl text-gray-300 mb-8">Setting up your {selectedTemplate?.name} module...</p>
              
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Provisioning infrastructure...</span>
                    <div className="animate-pulse w-4 h-4 bg-primary rounded-full"></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Installing dependencies...</span>
                    <div className="animate-pulse w-4 h-4 bg-primary rounded-full"></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Configuring services...</span>
                    <div className="animate-pulse w-4 h-4 bg-primary rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Complete */}
        {deploymentStep === 5 && (
          <div className="text-center">
            <div className="max-w-2xl mx-auto">
              <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-white mb-4">Deployment Complete!</h1>
              <p className="text-xl text-gray-300 mb-8">
                Your {selectedTemplate?.name} module has been successfully deployed.
              </p>
              
              <div className="bg-gray-800 rounded-xl p-6 mb-8">
                <h3 className="text-lg font-bold text-white mb-4">Module Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Module ID:</span>
                    <span className="text-white font-mono">mod-{Math.random().toString(36).substr(2, 9)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className="text-green-400">Running</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Endpoint:</span>
                    <span className="text-primary">https://api.orbitmind.com/modules/...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Region:</span>
                    <span className="text-white">{regions.find(r => r.id === deploymentConfig.region)?.name}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/services')}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-600 transition-colors"
                >
                  View in Services Hub
                </button>
                <button
                  onClick={() => {
                    setDeploymentStep(1);
                    setSelectedTemplate(null);
                    setDeploymentConfig({
                      name: '',
                      description: '',
                      environment: 'production',
                      region: 'us-east-1',
                      instanceType: 't3.medium',
                      autoScaling: true,
                      monitoring: true
                    });
                  }}
                  className="bg-gray-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-600 transition-colors"
                >
                  Deploy Another Module
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeployModule;